import {
	getExternalMessageDmIntegrationInfo,
	type ExternalMessageDmIntegrationInfo,
} from './thirdPartyMessaging';

export type ThirdPartyIntegrationRow = {
	externalId: string;
	integrationId: string;
	product: string;
};

export type ThirdPartyIntegrationEntry = {
	email: string | null | undefined;
	integrationId: string;
	externalId: string;
	product: string;
};

const normalizeProductKey = (product: string) => product.trim().toLowerCase();

function uniqueRowsByFirstOccurrence(
	integrations: ThirdPartyIntegrationRow[] = [],
): ThirdPartyIntegrationRow[] {
	const seen = new Set<string>();
	const out: ThirdPartyIntegrationRow[] = [];
	for (const row of integrations) {
		const key = normalizeProductKey(row.product);
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		out.push(row);
	}
	return out;
}

export function buildThirdPartyIntegrationsByProduct(
	integrations: ThirdPartyIntegrationRow[] | null | undefined,
	email: string | null | undefined,
): Record<string, ThirdPartyIntegrationEntry> {
	const rows = uniqueRowsByFirstOccurrence(integrations ?? []);
	const byProduct: Record<string, ThirdPartyIntegrationEntry> = {};
	for (const row of rows) {
		byProduct[normalizeProductKey(row.product)] = {
			email,
			externalId: row.externalId,
			integrationId: row.integrationId,
			product: row.product,
		};
	}
	return byProduct;
}

export const DEFAULT_MESSAGE_INTEGRATION_ID_ORDER: readonly string[] = ['slack', 'teams'];

export function resolveExternalMessageFromIntegrations(
	integrations: ThirdPartyIntegrationRow[] | null | undefined,
	email: string | null | undefined,
	integrationIdOrder: readonly string[] = DEFAULT_MESSAGE_INTEGRATION_ID_ORDER,
):
	| {
			entry: ThirdPartyIntegrationEntry;
			dm: ExternalMessageDmIntegrationInfo;
	  }
	| undefined {
	const rows = uniqueRowsByFirstOccurrence(integrations ?? []);

	for (const id of integrationIdOrder) {
		const row = rows.find((r) => r.integrationId === id);
		if (!row) {
			continue;
		}
		const entry: ThirdPartyIntegrationEntry = {
			email,
			externalId: row.externalId,
			integrationId: row.integrationId,
			product: row.product,
		};
		const dm = getExternalMessageDmIntegrationInfo(row.integrationId, {
			email: entry.email,
			externalId: entry.externalId,
		});
		if (dm) {
			return { entry, dm };
		}
	}

	return undefined;
}
